import {
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@/ui/primitives';
import { type DataGridProps } from '../../primitives/DataGrid';
import type { GridQueryInput, GridQueryResult } from './gridUtils';
import type { QueryKey } from '@tanstack/react-query';

export type BuildServerDataGridStateParams<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  columns: DataGridProps['columns'];
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  restoreFilterModel?: boolean;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  isAuthenticated?: boolean;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  active?: boolean;
  skeletonGetRowHeight?: DataGridProps['getRowHeight'] | null;
  getRowHeight?: DataGridProps['getRowHeight'];
  preservePreviousData?: boolean;
};

export const buildServerDataGridStateParams = <T extends GridValidRowModel>({
  autoHeight,
  active,
  columnVisibilityModel,
  columnVisibilityPreset,
  columnVisibilityPresets,
  columns,
  filteredNoRowsText,
  fixedFilters,
  getRowHeight,
  height,
  initialData,
  initialFilterModel,
  initialPageSize,
  initialSortModel,
  isAuthenticated,
  noRowsText,
  onDataUpdatedAt,
  onInvalidate,
  onQueryInput,
  onQueryMeta,
  onRefetch,
  onRowsChange,
  organisationId,
  pageSizeOptions,
  persistKey,
  preservePreviousData,
  projectId,
  queryFn,
  queryKey,
  queryKeyPrefix,
  queryPath,
  restoreFilterModel,
  refetchIntervalMs,
  refetchOnMount,
  refetchOnWindowFocus,
  search,
  skeletonGetRowHeight,
  staleTimeMs,
  syncStateToUrl,
}: BuildServerDataGridStateParams<T>): BuildServerDataGridStateParams<T> => ({
  queryPath,
  queryFn,
  initialData,
  columns,
  pageSizeOptions,
  initialPageSize,
  height,
  noRowsText,
  filteredNoRowsText,
  onRowsChange,
  onQueryMeta,
  onDataUpdatedAt,
  fixedFilters,
  search,
  initialSortModel,
  initialFilterModel,
  queryKeyPrefix,
  queryKey,
  restoreFilterModel,
  onRefetch,
  onInvalidate,
  onQueryInput,
  isAuthenticated,
  organisationId,
  projectId,
  persistKey,
  syncStateToUrl,
  autoHeight,
  active,
  columnVisibilityModel,
  columnVisibilityPresets,
  columnVisibilityPreset,
  refetchIntervalMs,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  skeletonGetRowHeight,
  getRowHeight,
  preservePreviousData,
});
